import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../Layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../Common/LoadingSpinner';
import './Contacts.css';
import { requestHelpers } from '../../services/api';
import axios from 'axios';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Helper component for highlighting text
const HighlightText = ({ text, highlight }) => {
  if (!highlight || !text) return text;

  const parts = text.toString().split(new RegExp(`(${highlight})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} className="highlight-text" style={{ backgroundColor: '#fff3cd', padding: '0 2px', borderRadius: '2px' }}>{part}</span>
        ) : (
          part
        )
      )}
    </span>
  );
};

const Contacts = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    tags: 'all',
    status: 'all'
  });
  const [stats, setStats] = useState({
    unsubscribed: 0,
    interested: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters(prev => ({ ...prev, search: debouncedSearchTerm }));
  }, [debouncedSearchTerm]);

  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    email: '',
    tags: [],
    notes: '',
    listName: ''
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);

  const hasFetched = React.useRef(false);

  // Define fetchContacts before useEffect to use it in deps
  const fetchContacts = React.useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setInitialLoading(true);
      setIsFetching(true);
      const params = { page, limit: 50 };
      if (filters.search) params.search = filters.search;
      if (filters.tags !== 'all') params.tags = filters.tags;
      if (filters.status !== 'all') params.status = filters.status;

      const response = await requestHelpers.get('/contacts', { params });
      if (response.success) {
        setContacts(response.data?.contacts || []);
        setTotalPages(response.data?.pagination?.totalPages || 1);
        setTotalContacts(response.data?.pagination?.totalContacts || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch contacts');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInitialLoading(false);
      setIsFetching(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchContacts(hasFetched.current);
    hasFetched.current = true;
  }, [fetchContacts]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await requestHelpers.get('/contacts/stats');
        if (response.success) {
          const unsubCount = response.data.byStatus?.find(s => s._id === 'unsubscribed')?.count || 0;
          const interestedCount = response.data.topTags?.find(t => t._id.toLowerCase() === 'interested')?.count || 0;
          setStats({ 
            unsubscribed: unsubCount,
            interested: interestedCount
          });
        }
      } catch (error) {
        console.error('Failed to fetch contact stats:', error);
      }
    };
    fetchStats();
  }, []);

  // Group contacts by list name
  const groupedContacts = contacts.reduce((acc, contact) => {
    const list = contact.listName || 'Uncategorized';
    if (!acc[list]) acc[list] = [];
    acc[list].push(contact);
    return acc;
  }, {});

  // Manage collapse states for each list
  const [collapsedLists, setCollapsedLists] = useState({});

  const toggleList = (listName) => {
    setCollapsedLists((prev) => ({
      ...prev,
      [listName]: !prev[listName],
    }));
  };




  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const response = await requestHelpers.post('/contacts', newContact);
      if (response.success) {
        toast.success('Contact added successfully!');
        setShowAddModal(false);
        setNewContact({ name: '', phone: '', email: '', tags: [], notes: '' });
        fetchContacts();
      } else {
        throw new Error(response.message || 'Failed to add contact');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };


  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      const response = await requestHelpers.delete(`/contacts/${contactId}`);
      if (response.success) {
        setContacts((prev) => prev.filter((c) => c._id !== contactId));
        toast.success('Contact deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete contact');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditContact = async (e) => {
    e.preventDefault();
    try {
      const response = await requestHelpers.put(`/contacts/${editContact._id}`, editContact);
      if (response.success) {
        toast.success('Contact updated successfully!');
        setShowEditModal(false);
        setEditContact(null);
        fetchContacts();
      } else {
        throw new Error(response.message || 'Failed to update contact');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };


  const handleBulkAction = async (action, format = 'csv') => {
    if (selectedContacts.length === 0) {
      toast.warning('Please select contacts first');
      return;
    }
    try {
      switch (action) {
        case 'delete': {
          if (!window.confirm(`Delete ${selectedContacts.length} contacts?`)) return;
          const response = await requestHelpers.post('/contacts/bulk-delete', {
            contactIds: selectedContacts,
          });
          if (response.success) {
            setContacts((prev) => prev.filter((c) => !selectedContacts.includes(c._id)));
            setSelectedContacts([]);
            toast.success('Contacts deleted successfully');
          } else {
            throw new Error(response.message || 'Failed bulk delete');
          }
          break;
        }
        case 'export': {
          try {
            const query = new URLSearchParams();
            query.append('format', 'csv');
            selectedContacts.forEach(id => query.append('contactIds', id));

            const API_URL = process.env.REACT_APP_API_URL || '/api';
            const res = await axios.get(`${API_URL}/contacts/export?${query.toString()}`, {
              responseType: 'blob',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            // Determine filename based on selected contacts
            let filename = 'contacts_export';
            if (selectedContacts.length > 0) {
              // Find the list name of the first selected contact
              const firstContact = contacts.find(c => c._id === selectedContacts[0]);
              if (firstContact && firstContact.listName) {
                // Check if all selected contacts are from the same list
                const allSameList = selectedContacts.every(id => {
                  const contact = contacts.find(c => c._id === id);
                  return contact && contact.listName === firstContact.listName;
                });

                if (allSameList) {
                  filename = firstContact.listName;
                }
              }
            }

            const blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${filename}.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            toast.success(`Contacts exported as ${format.toUpperCase()}`);
          } catch (err) {
            toast.error(err.message);
          }

          break;
        }
        default:
          break;
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = "name,phone,email,tags,notes\n" +
      "John Doe,919876543210,john@example.com,\"lead,vip\",Interested in automation\n" +
      "Jane Smith,919876543211,jane@example.com,customer,Follow up next week";
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_contacts.csv");
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportContacts = async (e) => {
    e.preventDefault();
    const file = e.target.file.files[0];
    if (!file || !newContact.listName) {
      toast.error('Please provide a list name and CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('listName', newContact.listName);
    try {
      const response = await requestHelpers.post('/contacts/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.success) {
        const { importedCount, duplicatesCount, errorsCount } = response.data || {};
        if (importedCount > 0) {
          toast.success(
            `Import completed: ${importedCount} contact(s) imported successfully! ` +
            `${duplicatesCount > 0 ? `(${duplicatesCount} duplicates skipped) ` : ''}` +
            `${errorsCount > 0 ? `(${errorsCount} error(s) skipped)` : ''}`
          );
        } else if (duplicatesCount > 0 || errorsCount > 0) {
          toast.warning(
            `No new contacts imported. ` +
            `${duplicatesCount > 0 ? `${duplicatesCount} duplicates skipped. ` : ''}` +
            `${errorsCount > 0 ? `${errorsCount} error(s) failed validation.` : ''}`
          );
        } else {
          toast.success('Contacts imported successfully!');
        }
        setShowImportModal(false);
        fetchContacts();
      } else {
        throw new Error(response.message || 'Failed to import contacts');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };


  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSelectAll = (listName = null) => {
    if (listName) {
      // List-wise selection
      const listContacts = groupedContacts[listName] || [];
      const listContactIds = listContacts.map(c => c._id);

      // Check if all contacts in this list are already selected
      const allSelected = listContactIds.every(id => selectedContacts.includes(id));

      if (allSelected) {
        // Deselect all in this list
        setSelectedContacts(prev => prev.filter(id => !listContactIds.includes(id)));
      } else {
        // Select all in this list (add missing ones)
        setSelectedContacts(prev => {
          const newSelection = [...prev];
          listContactIds.forEach(id => {
            if (!newSelection.includes(id)) {
              newSelection.push(id);
            }
          });
          return newSelection;
        });
      }
    } else {
      // Global selection (legacy or fallback)
      if (selectedContacts.length === contacts.length) {
        setSelectedContacts([]);
      } else {
        setSelectedContacts(contacts.map(contact => contact._id));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSourceBadge = (source) => {
    const badges = {
      manual: 'badge-primary',
      import: 'badge-success',
      form: 'badge-info',
      api: 'badge-warning'
    };
    return badges[source] || 'badge-secondary';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'badge-success';
      case 'unsubscribed': return 'badge-secondary';
      case 'blocked': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  if (initialLoading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    );
  }


  return (
    <DashboardLayout>
      <div className="contacts-page">
        <div className="page-header">
          <div className="header-content">
            <h1>Contacts</h1>
            <p className="header-subtitle">
              Manage your contact database {isFetching && <span className="updating-status">(Updating...)</span>}
              {stats.unsubscribed > 0 && (
                <span className="stat-badge unsubscribed" onClick={() => setFilters({ ...filters, status: 'unsubscribed', tags: 'all' })} title="Filter Unsubscribed">
                  <i className="fas fa-user-minus"></i> {stats.unsubscribed} Unsubscribed
                </span>
              )}
              {stats.interested > 0 && (
                <span className="stat-badge interested" onClick={() => setFilters({ ...filters, tags: 'interested', status: 'all' })} title="Filter Interested">
                  <i className="fas fa-star"></i> {stats.interested} Interested
                </span>
              )}
            </p>
            {user?.whatsappNumber && (
              <div className="header-number-badge mt-2" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                <i className="fab fa-whatsapp"></i>
                <span>Linked to: {user.whatsappNumber}</span>
              </div>
            )}
          </div>
          <div className="header-actions">
            <button
               className="btn btn-ghost btn-sm"
               onClick={downloadSampleCSV}
               title="Download sample CSV file for reference"
               style={{ marginRight: '8px' }}
            >
              <i className="fas fa-file-download"></i>
              Sample CSV
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-upload"></i>
              Import
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <i className="fas fa-plus"></i>
              Add Contact
            </button>
          </div>
        </div>

        <div className="contacts-filters">
          <div className="filters-row">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={filters.tags}
              onChange={(e) => setFilters({ ...filters, tags: e.target.value })}
              className="tags-filter"
            >
              <option value="all">All Tags</option>
              <option value="customer">Customer</option>
              <option value="lead">Lead</option>
              <option value="vip">VIP</option>
              <option value="interested">Interested</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="status-filter"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          {selectedContacts.length > 0 && (
            <div className="bulk-actions">
              <span className="selected-count">
                {selectedContacts.length} selected
              </span>
              <div className="bulk-buttons">
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleBulkAction('export')}
                >
                  <i className="fas fa-download"></i>
                  Export
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleBulkAction('delete')}
                >
                  <i className="fas fa-trash"></i>
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="contacts-table-container">
          <div className="table-responsive">
            {/* <table className="contacts-table">
              <thead>
                <tr>
                  <th>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedContacts.length === contacts.length && contacts.length > 0}
                        onChange={handleSelectAll}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Tags</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Last Contacted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact.id}>
                    <td>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact._id)}
                          onChange={() => handleSelectContact(contact._id)}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </td>
                    <td>
                      <div className="contact-info">
                        <div className="contact-avatar">
                          {contact.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="contact-details">
                          <div className="contact-name">{contact.name}</div>
                          <div className="contact-email">{contact.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="contact-phone">{contact.phone}</span>
                    </td>
                    <td>
                      <div className="contact-tags">
                        {contact.tags.map(tag => (
                          <span key={tag} className="tag-badge">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`source-badge ${getSourceBadge(contact.source)}`}>
                        {contact.source}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadge(contact.status)}`}>
                        {contact.status}
                      </span>
                    </td>
                    <td>
                      <span className="last-contacted">
                        {formatDate(contact.lastContacted)}
                      </span>
                    </td>
                    <td>
                      <div className="contact-actions">
                        <button className="action-btn" title="Edit"       onClick={() => {
        setEditContact(contact);
        setShowEditModal(true);
      }}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="action-btn" title="Message">
                          <i className="fas fa-comment"></i>
                        </button>
                        <button 
                          className="action-btn delete-btn" 
                          title="Delete"
                          onClick={() => handleDeleteContact(contact._id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table> */}
            <div className="contacts-table-container">
              {Object.entries(groupedContacts).map(([listName, listContacts]) => (
                <div key={listName} className="contact-list-section">
                  <div
                    className="list-header"
                    onClick={() => toggleList(listName)}
                  >
                    <h3>
                      <i
                        className={`fas ${collapsedLists[listName] ? 'fa-chevron-right' : 'fa-chevron-down'
                          }`}
                        style={{ marginRight: '8px' }}
                      ></i>
                      {listName} ({listContacts.length})
                    </h3>
                  </div>

                  {!collapsedLists[listName] && (
                    <div className="table-responsive">
                      <table className="contacts-table">
                        <thead>
                          <tr>
                            <th>
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={
                                    listContacts.length > 0 &&
                                    listContacts.every(c => selectedContacts.includes(c._id))
                                  }
                                  onChange={() => handleSelectAll(listName)}
                                />
                                <span className="checkmark"></span>
                              </label>
                            </th>
                            <th>Contact</th>
                            <th>Phone</th>
                            <th>Tags</th>
                            <th>Source</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listContacts.map((contact) => (
                            <tr key={contact._id}>
                              <td>
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={selectedContacts.includes(contact._id)}
                                    onChange={() => handleSelectContact(contact._id)}
                                  />
                                  <span className="checkmark"></span>
                                </label>
                              </td>
                              <td>
                                <div className="contact-info">
                                  <div className="contact-avatar">
                                    {contact.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="contact-details">
                                    <div className="contact-name">
                                      <HighlightText text={contact.name} highlight={debouncedSearchTerm} />
                                    </div>
                                    <div className="contact-email">
                                      <HighlightText text={contact.email} highlight={debouncedSearchTerm} />
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <HighlightText text={contact.phone} highlight={debouncedSearchTerm} />
                              </td>
                              <td>
                                <div className="contact-tags">
                                  {contact.tags.map((tag) => (
                                    <span key={tag} className="tag-badge">
                                      <HighlightText text={tag} highlight={debouncedSearchTerm} />
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <span className={`source-badge ${getSourceBadge(contact.source)}`}>
                                  {contact.source}
                                </span>
                              </td>
                              <td>
                                <div className="contact-actions">
                                  <button
                                    className="action-btn"
                                    title="Edit"
                                    onClick={() => {
                                      setEditContact(contact);
                                      setShowEditModal(true);
                                    }}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="action-btn delete-btn"
                                    title="Delete"
                                    onClick={() => handleDeleteContact(contact._id)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>

        {contacts.length > 0 && totalPages > 1 && (
          <div className="pagination-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '15px 20px', background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '15px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Showing Page <strong>{page}</strong> of <strong>{totalPages}</strong> (<strong>{totalContacts}</strong> total contacts)
            </span>
            <div className="pagination-buttons" style={{ display: 'flex', gap: '10px' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
                style={{ opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
                style={{ opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}

        {contacts.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-address-book"></i>
            <h3>No contacts found</h3>
            <p>Add your first contact or import from a CSV file</p>
            <div className="empty-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowAddModal(true)}
              >
                Add Contact
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowImportModal(true)}
              >
                Import Contacts
              </button>
            </div>
          </div>
        )}

        {/* Add Contact Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add New Contact</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleAddContact} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>List Name *</label>
                    <input
                      type="text"
                      value={newContact.listName}
                      onChange={(e) => setNewContact({ ...newContact, listName: e.target.value })}
                      placeholder="Enter a unique list name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      placeholder="Enter contact name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      placeholder="Enter phone number"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    placeholder="Enter tags (comma separated)"
                    onChange={(e) => setNewContact({
                      ...newContact,
                      tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                    })}
                  />
                  <small>Separate multiple tags with commas</small>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={newContact.notes}
                    onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                    placeholder="Add notes about this contact"
                    rows="3"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Contact
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Contact Modal */}
        {showEditModal && editContact && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Edit Contact</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowEditModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleEditContact} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>List Name *</label>
                    <input
                      type="text"
                      value={editContact.listName || ''}
                      onChange={(e) => setEditContact({ ...editContact, listName: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={editContact.name}
                      onChange={(e) => setEditContact({ ...editContact, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={editContact.phone}
                      onChange={(e) => setEditContact({ ...editContact, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editContact.email || ''}
                    onChange={(e) => setEditContact({ ...editContact, email: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    value={editContact.tags?.join(', ') || ''}
                    onChange={(e) => setEditContact({
                      ...editContact,
                      tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                    })}
                  />
                  <small>Separate multiple tags with commas</small>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={editContact.notes || ''}
                    onChange={(e) => setEditContact({ ...editContact, notes: e.target.value })}
                    rows="3"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update Contact
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* Import Modal */}
        {showImportModal && (
          <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Import Contacts</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowImportModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleImportContacts} className="import-form">
                <div className="import-instructions">
                  <h4>Import Instructions</h4>
                  <ul>
                    <li>Upload a CSV file with contacts</li>
                    <li>Required columns: Name, Phone</li>
                    <li>Optional columns: Email, Tags, Notes</li>
                    <li>Maximum 1000 contacts per import</li>
                    <li className="download-sample">
                      <button 
                        type="button" 
                        className="btn-link" 
                        onClick={downloadSampleCSV}
                        style={{ background: 'none', border: 'none', color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}
                      >
                        Download Sample CSV
                      </button>
                    </li>
                  </ul>
                </div>

                <div className="form-group">
                  <label>List Name *</label>
                  <input
                    type="text"
                    name="listName"
                    placeholder="Enter list name for this import"
                    required
                    onChange={(e) => setNewContact({ ...newContact, listName: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>CSV File *</label>
                  <input
                    type="file"
                    name="file"
                    accept=".csv"
                    required
                    className="file-input"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowImportModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Import Contacts
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Contacts;